import React, { useState } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Icon from '../AppIcon';

/**
 * Modal for quick action links in messaging
 * Handles employer actions (schedule interview, request portfolio, send offer)
 * and VA actions (share portfolio, ask about role, confirm availability)
 */
const QuickActionsModal = ({ 
  isOpen, 
  onClose, 
  actionType, 
  recipientName,
  isEmployer,
  onSubmit 
}) => {
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    location: '',
    notes: '',
    portfolioLink: '',
    questions: '',
    availability: '',
    offerDetails: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      setFormData({
        date: '',
        time: '',
        location: '',
        notes: '',
        portfolioLink: '',
        questions: '',
        availability: '',
        offerDetails: ''
      });
      onClose();
    } catch (error) {
      console.error('Error submitting quick action:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const renderContent = () => {
    switch (actionType) {
      case 'interview':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Schedule an Interview</h3>
            <p className="text-sm text-gray-600">with {recipientName}</p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Interview Date
                </label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time
                </label>
                <Input
                  type="time"
                  value={formData.time}
                  onChange={(e) => handleInputChange('time', e.target.value)}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location / Meeting Link
              </label>
              <Input
                type="text"
                placeholder="e.g., Zoom link or office address"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Any additional information about the interview..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>
          </div>
        );

      case 'portfolio':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Request Portfolio</h3>
            <p className="text-sm text-gray-600">Ask {recipientName} to share their portfolio</p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                A message will be sent asking to view their portfolio or work samples.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Message (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Add any specific requirements or focus areas..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>
          </div>
        );

      case 'offer':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Send Job Offer</h3>
            <p className="text-sm text-gray-600">to {recipientName}</p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                Send a job offer for the position. Include details about compensation, start date, and next steps.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Offer Details
              </label>
              <textarea
                value={formData.offerDetails}
                onChange={(e) => handleInputChange('offerDetails', e.target.value)}
                placeholder="Include salary, benefits, start date, and any other relevant offer details..."
                rows={4}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>
          </div>
        );

      case 'ask':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Ask About the Role</h3>
            <p className="text-sm text-gray-600">to {recipientName}</p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                Share your questions about the role, responsibilities, or company.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Questions
              </label>
              <textarea
                value={formData.questions}
                onChange={(e) => handleInputChange('questions', e.target.value)}
                placeholder="What would you like to know about the role or company?..."
                rows={4}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>
          </div>
        );

      case 'confirm':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Confirm Availability</h3>
            <p className="text-sm text-gray-600">to {recipientName}</p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                Confirm your availability and readiness for the position.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Availability Details
              </label>
              <textarea
                value={formData.availability}
                onChange={(e) => handleInputChange('availability', e.target.value)}
                placeholder="Share when you can start, hours per week, timezone, or any scheduling preferences..."
                rows={4}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>
          </div>
        );

      case 'share_portfolio':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Share Your Portfolio</h3>
            <p className="text-sm text-gray-600">with {recipientName}</p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                Share links to your portfolio, GitHub, or work samples.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Portfolio Link
              </label>
              <Input
                type="url"
                placeholder="https://yourportfolio.com"
                value={formData.portfolioLink}
                onChange={(e) => handleInputChange('portfolioLink', e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Links (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="GitHub, LinkedIn, or other relevant links..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-96 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Quick Action</h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {renderContent()}

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Icon name="Send" size={16} className="mr-2" />
                  Send
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickActionsModal;
